import { getBrowserEnv } from "./tools";
import { myInit, wxInit } from "./mini-program";
import { key as unionLoginAppId } from "./union-axios";
import { getHrefWithoutHostLoginState } from "./host-login-state";

/**
 * 宿主（小程序）已声明未登录时的主动登录入口。
 *
 * 不能走 getCurrentUid() / 联登 SDK 的自动跳登录：webview 内的联登会话在小程序退登后
 * 仍然有效，问后端会把已登出的用户判回已登录。这里直接把用户交还给宿主登录页，登录成功后
 * 宿主会用新的 otp/uid 重新加载 webview（hostLoginState 变为 1），页面自然恢复登录态。
 * 跳转地址与联登 SDK 的 getMiniProgramLoginUrl 保持一致。
 *
 * 从 useConfigPage 抽到这里，是因为专题页之外的页面（exChangeDetail、charge-vip、
 * plug-and-charge 等）也需要它：H5 侧 goToPage / goToPageV2 拼跳转时 appId 恒为空，
 * 这些页面会绕过小程序的登录门禁被直接打开，必须自己把用户交还给登录页。
 *
 * 放在独立文件而不是 host-login-state.ts 里，是为了保住那个模块的零业务依赖
 * （它要在入口最早处执行，不能提前拉起 tools / mini-program / union-axios 的模块图）。
 *
 * @param forceLogin 取消登录后是否要退出这个 webview 页面。
 * 小程序侧无法自己判断——同一个登录页跳转对它来说长得完全一样，只有 H5 知道当前页面
 * 是否强制登录（专题页有 loginRequired 配置，其余页面一律强制），因此需要显式带过去。
 * forceLogin=1 时取消登录会退出页面；否则降级为游客态留在原地
 * （见小程序 webpage/index.tsx 的 loginSuccess 处理）。
 *
 * **注意**：forceLogin 描述的是「收到 loginSuccess 信号的那个页面」该不该退出，而
 * 小程序侧是靠 `pages[pages.length - 2].setData()` 通知「登录页的上一页」。因此
 * forceLogin 的取值必须和 options.replace 一起看，见下方说明。
 *
 * @param options.replace 用 redirectTo 替换当前页打开登录页，而不是 navigateTo 压栈。
 *
 * 两种模式对应两种「谁是登录页的上一页」：
 *
 *   replace=false（默认，navigateTo 压栈）——发起登录的页面自己就是要刷新的页面。
 *     栈：[发起页, 登录页]，上一页 = 发起页本身，信号送回自己。
 *     配 backOnly=1，登录成功后 navigateBack 回发起页并由它重建 webview。
 *     用于页面主动拉起登录（专题页点登录条 / loginRequired=1 强制登录）。
 *
 *   replace=true（redirectTo 替换）——发起登录的是中间页，它不是最终要刷新的页面。
 *     栈：[上游页, 登录页]（中间页被登录页顶掉），上一页 = 上游页，信号送给它。
 *     不带 backOnly，登录页走 webviewUrl 分支 redirectTo 一个新的中间页容器
 *     （带 code，中间页本身也恢复登录态）；用户返回时上游页命中 loginSuccess
 *     并重建自己。用于守卫拦截（useHostLoginGuard）。
 *
 *     这也是 4dc8d98 之前联登 SDK 一直在做的事（见 @xpeng/union-login 的
 *     redirectToLogin，全包只用 miniProgram.redirectTo），那时「专题页 → 二级页 →
 *     登录 → 返回」是正常的；改成 navigateTo 压栈后信号被中间页吃掉才出现回归。
 *
 *     replace=true 时 forceLogin 应传 false：收到信号的是上游页，该不该退出要由
 *     它自己的性质决定，而小程序侧本来就有这个判断（webpage 页的 this.isConfigPage）——
 *     上游是免登专题页则降级游客态留在原地，是其它必登 H5 则退出。中间页本身已经被
 *     登录页替换掉，"需要登录的页面取消登录则退出"这一条已经天然满足。
 */
export const redirectToHostLogin = (
  forceLogin: boolean,
  options?: { replace?: boolean },
): void => {
  const { isWXApp, isMyApp } = getBrowserEnv();
  const replace = Boolean(options?.replace);
  // backOnly=1：告知登录页登录成功后回退到发起登录的原 webview 页面并触发其刷新，
  // 而不是新开一个 webview 页面替换登录页——否则旧的未登录态页面会遗留在页面栈里
  // （见 webpage/index.tsx 的 loginSuccess 处理）。
  // replace 模式下不能带它：那时发起页已经被登录页替换掉，不存在"遗留"问题，
  // 而且必须让登录页走 webviewUrl 分支重开中间页，否则 back 会直接跳过它。
  const url = `/pages/user/login?appId=${unionLoginAppId}&webviewUrl=${encodeURIComponent(
    getHrefWithoutHostLoginState(),
  )}${replace ? "" : "&backOnly=1"}${forceLogin ? "&forceLogin=1" : ""}`;
  if (isWXApp) {
    wxInit().then((wx) =>
      replace
        ? wx.miniProgram.redirectTo({ url })
        : wx.miniProgram.navigateTo({ url }),
    );
    return;
  }
  if (isMyApp) {
    myInit().then((my) =>
      replace ? my.redirectTo({ url }) : my.navigateTo({ url }),
    );
    return;
  }
  console.warn("[host-login-redirect] 非小程序环境不应出现宿主登录态参数", url);
};
